import { useCallback } from 'react'
import { useApolloClient } from '@apollo/client'

import { DebitCardTransactionsV2FragmentDoc } from 'graphql/merchant-portal/queries/debitCardTransactions'
import { rvUpdatedDebitCardTransactions } from 'graphql/reactiveVariables'
import { DebitCardTransactionStatusV2 } from 'types/gql-global-types'
import { GetDebitCardTransactions_getDebitCardTransactionsV2_transactions as DebitCardTransaction } from 'types/gql-types/GetDebitCardTransactions'
import { getCurrentTime } from 'pages/Dashboard/utils/timeUtils'

import { useReadUpdatedOutstandingTransactionsLocalCache } from './useReadUpdatedOutstandingTransactionsLocalCache'
import { LocalStateOutstandingTransaction } from './useUpdatedOutstandingTransactions.types'
import { filterOutdatedTransactions } from './useUpdatedOutstandingTransactions.utils'
import { useWriteUpdatedOutstandingTransactionsLocalCache } from './useWriteUpdatedOutstandingTransactionsLocalCache'

export const useUpdatedOutstandingTransactions = (
  debitCardTransactionUuid: string
) => {
  const { cache } = useApolloClient()
  const { updatedOutstandingTransactions } =
    useReadUpdatedOutstandingTransactionsLocalCache()
  const { updateCache } = useWriteUpdatedOutstandingTransactionsLocalCache()

  const updatedDebitCardTransactions = rvUpdatedDebitCardTransactions()

  const cachedTransaction = cache.readFragment<DebitCardTransaction>({
    fragment: DebitCardTransactionsV2FragmentDoc,
    id: cache.identify({
      id: debitCardTransactionUuid,
      __typename: 'DebitCardTransactionV2',
    }),
  })

  const existingOptimisticTransaction = updatedDebitCardTransactions.find(
    (optimisticTransaction) =>
      optimisticTransaction.id === debitCardTransactionUuid
  )

  const cacheCurrentUpdatedOutstandingTransaction = useCallback(
    ({ update }: { update: Partial<DebitCardTransaction> }) => {
      const transaction = existingOptimisticTransaction ?? cachedTransaction
      if (
        !transaction ||
        transaction.status !== DebitCardTransactionStatusV2.APPROVED
      ) {
        return
      }

      const newTransaction: LocalStateOutstandingTransaction = {
        transaction: { ...transaction, ...update },
        timestamp: getCurrentTime().valueOf(),
      }
      const updatedOutstandingTransactionsWithoutCurrent =
        updatedOutstandingTransactions.filter(
          (updatedOutstandingTransaction) =>
            updatedOutstandingTransaction.transaction.id !==
            debitCardTransactionUuid
        )

      updateCache(
        filterOutdatedTransactions([
          ...updatedOutstandingTransactionsWithoutCurrent,
          newTransaction,
        ])
      )
    },
    [
      cachedTransaction,
      existingOptimisticTransaction,
      updatedOutstandingTransactions,
      updateCache,
      debitCardTransactionUuid,
    ]
  )

  return {
    cacheCurrentUpdatedOutstandingTransaction,
  }
}
